import React, { useEffect, useRef, useState } from 'react';
import { EditorView, keymap } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { javascript } from '@codemirror/lang-javascript';
// 使用浅色主题，不引入 oneDark
import { history } from '@codemirror/commands';
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching } from '@codemirror/language';
import { lineNumbers } from '@codemirror/view';
import { Button, Space, Tooltip } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';

/**
 * CodeMirror 6 代码编辑器组件
 * @param {string} value - 编辑器内容
 * @param {function} onChange - 内容变化回调
 * @param {string} placeholder - 占位文本
 * @param {number} defaultHeight - 默认高度（px）
 * @param {boolean} readOnly - 是否只读
 * @param {boolean} autoHeight - 是否自动填满父容器高度
 */
function CodeEditor({ 
  value = '', 
  onChange, 
  placeholder = '', 
  defaultHeight = 400,
  readOnly = false,
  autoHeight = false
}) {
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // 校验高度值，确保返回有效数字
  const getValidHeight = (height) => {
    const parsed = Number(height);
    return isNaN(parsed) || parsed <= 0 ? 400 : parsed;
  };
  
  const [editorHeight, setEditorHeight] = useState(() => getValidHeight(defaultHeight));

  // 监听 defaultHeight 变化，同步更新高度状态
  useEffect(() => {
    if (!autoHeight && !isFullscreen) {
      setEditorHeight(getValidHeight(defaultHeight));
    }
  }, [defaultHeight, autoHeight, isFullscreen]);

  // 调试：打印高度信息
  useEffect(() => {
    console.log('[CodeEditor] defaultHeight:', defaultHeight);
    console.log('[CodeEditor] editorHeight:', editorHeight);
    console.log('[CodeEditor] autoHeight:', autoHeight);
  }, []);

  // 计算自适应高度
  const calculateAutoHeight = () => {
    if (!autoHeight || !containerRef.current) return;
    
    const container = containerRef.current;
    const parent = container.parentElement;
    if (!parent) return;
    
    // 获取父容器可用高度
    const parentRect = parent.getBoundingClientRect();
    const siblings = Array.from(parent.children).filter(el => el !== container);
    let siblingsHeight = 0;
    
    siblings.forEach(sibling => {
      const style = window.getComputedStyle(sibling);
      const marginTop = parseInt(style.marginTop) || 0;
      const marginBottom = parseInt(style.marginBottom) || 0;
      siblingsHeight += sibling.offsetHeight + marginTop + marginBottom;
    });
    
    // 减去工具栏高度和边距
    const toolbarHeight = 40;
    const padding = 32;
    const availableHeight = parentRect.height - siblingsHeight - toolbarHeight - padding;
    
    if (availableHeight > 200) {
      setEditorHeight(Math.min(availableHeight, 600));
    }
  };

  useEffect(() => {
    if (autoHeight) {
      calculateAutoHeight();
      window.addEventListener('resize', calculateAutoHeight);
      return () => window.removeEventListener('resize', calculateAutoHeight);
    }
  }, [autoHeight]);

  useEffect(() => {
    if (!editorRef.current) return;

    // 创建编辑器状态
    const state = EditorState.create({
      doc: value,
      extensions: [
        // 基础编辑功能
        lineNumbers(),
        history(),
        syntaxHighlighting(defaultHighlightStyle),
        bracketMatching(),
        
        // JavaScript 语言支持
        javascript(),
        
        // 键盘快捷键
        keymap.of([
          {
            key: 'Tab',
            run: (view) => {
              view.dispatch(view.state.replaceSelection('  '));
              return true;
            },
          },
        ]),
        
        // 更新监听
        EditorView.updateListener.of((update) => {
          if (update.docChanged && onChange) {
            onChange(update.state.doc.toString());
          }
        }),
        
        // 自定义浅色主题
        EditorView.theme({
          '&': {
            fontSize: '13px',
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            backgroundColor: '#ffffff',
          },
          '.cm-editor': {
            height: '100%',
            backgroundColor: '#ffffff',
          },
          '.cm-content': {
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            minHeight: '100%',
            caretColor: '#000',
          },
          '.cm-gutters': {
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            fontSize: '12px',
            backgroundColor: '#f5f5f5',
            borderRight: '1px solid #ddd',
            minHeight: '100%',
            color: '#666',
          },
          '.cm-activeLineGutter': {
            backgroundColor: '#e8e8e8',
          },
          '.cm-activeLine': {
            backgroundColor: '#f0f7ff',
          },
          '.cm-scroller': {
            overflow: 'auto',
          },
          '.cm-cursor': {
            borderLeftColor: '#000',
          },
        }),
        
        readOnly ? EditorView.editable.of(false) : [],
      ],
    });

    // 创建编辑器视图
    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
    };
  }, []);

  // 同步外部 value 变化
  useEffect(() => {
    const view = viewRef.current;
    if (view && value !== view.state.doc.toString()) {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value },
      });
      // 修复：当外部 value 变化时（如样例脚本填充），需要触发 onChange 通知 Form
      if (onChange && value !== undefined) {
        onChange(value);
      }
    }
  }, [value, onChange]);

  // 调整高度
  const increaseHeight = () => {
    setEditorHeight(prev => Math.min(prev + 100, 800));
  };

  const decreaseHeight = () => {
    setEditorHeight(prev => Math.max(prev - 100, 200));
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      setEditorHeight(window.innerHeight - 200);
    } else {
      if (autoHeight) {
        calculateAutoHeight();
      } else {
        setEditorHeight(defaultHeight);
      }
    }
  };

  return (
    <div 
      ref={containerRef}
      style={{ 
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        bottom: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 9999 : 'auto',
        background: isFullscreen ? '#ffffff' : 'transparent',
        padding: isFullscreen ? '20px' : 0,
        display: 'flex',
        flexDirection: 'column',
        height: isFullscreen ? '100vh' : 'auto',
        flex: autoHeight ? 1 : 'none',
      }}
    >
      {/* 工具栏 */}
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          marginBottom: '8px',
          padding: isFullscreen ? '0 0 10px 0' : 0,
          borderBottom: isFullscreen ? '1px solid #e8e8e8' : 'none',
        }}
      >
        <Space>
          <Tooltip title="缩小">
            <Button 
              type="text" 
              size="small" 
              icon={<MinusOutlined />} 
              onClick={decreaseHeight}
              style={{ color: '#666' }}
            />
          </Tooltip>
          <Tooltip title="放大">
            <Button 
              type="text" 
              size="small" 
              icon={<PlusOutlined />} 
              onClick={increaseHeight}
              style={{ color: '#666' }}
            />
          </Tooltip>
          <Tooltip title={isFullscreen ? "退出全屏" : "全屏编辑"}>
            <Button 
              type="text" 
              size="small" 
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} 
              onClick={toggleFullscreen}
              style={{ color: '#666' }}
            />
          </Tooltip>
        </Space>
      </div>

      {/* 编辑器容器 */}
      <div 
        ref={editorRef}
        onBlur={() => {
          // 修复：编辑器失焦时，确保表单获取到最新值
          if (onChange && viewRef.current) {
            onChange(viewRef.current.state.doc.toString());
          }
        }}
        style={{ 
          height: `${editorHeight}px`,
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          overflow: 'auto',
          flex: isFullscreen ? 1 : 'none',
        }}
      />
    </div>
  );
}

export default CodeEditor;
